import { ethers } from 'ethers';
import {
  chainId,
  chainName,
  chainRPC,
  blockExplorer,
  curName,
  curSymbol,
  curDec,
} from '../constants';
import {
  NetworkError,
  NetworkSwitchingHandler,
  MetaMaskConnectivityChecker,
  RpcEndpointManager,
  NetworkErrorRecovery,
  ManualNetworkInstructions,
  defaultNetworkRecoveryOptions,
  NetworkRecoveryOptions,
} from '../utils/networkErrorHandling';

export interface NetworkInfo {
  chainId: string;
  name: string;
  isSupported: boolean;
  isSwarmDevnet: boolean;
  rpcUrl?: string;
  blockExplorerUrl?: string;
}

export interface NetworkServiceResult {
  success: boolean;
  error?: string;
  userGuidance?: string;
  recoveryActions?: string[];
  canRetry?: boolean;
  requiresManualAction?: boolean;
}

export class NetworkService {
  private provider: ethers.BrowserProvider | null = null;
  private developmentMode: boolean = false;
  private connectivityChecker: MetaMaskConnectivityChecker;
  private recoveryOptions: NetworkRecoveryOptions;

  constructor(recoveryOptions: Partial<NetworkRecoveryOptions> = {}) {
    this.developmentMode = process.env.NODE_ENV === 'development';
    this.connectivityChecker = MetaMaskConnectivityChecker.getInstance();
    this.recoveryOptions = {
      ...defaultNetworkRecoveryOptions,
      ...recoveryOptions,
    };
  }

  private async initializeProvider() {
    if (typeof window !== 'undefined' && window.ethereum) {
      this.provider = new ethers.BrowserProvider(window.ethereum);
    }
  }

  private async ensureProvider() {
    if (!this.provider) {
      await this.initializeProvider();
    }

    if (!this.provider) {
      throw new NetworkError(
        'MetaMask not available',
        'PROVIDER_NOT_AVAILABLE',
        { operation: 'ensureProvider' },
        false,
        'Please install MetaMask extension and refresh the page',
        ['Install MetaMask from metamask.io', 'Refresh the page', 'Try again']
      );
    }
  }

  /**
   * Get Swarm devnet configuration
   */
  private getSwarmDevnetConfig() {
    return {
      chainId: chainId,
      chainName: chainName,
      nativeCurrency: {
        name: curName,
        symbol: curSymbol,
        decimals: curDec,
      },
      rpcUrls: [chainRPC],
      blockExplorerUrls: [blockExplorer],
    };
  }

  /**
   * Get local development network configurations
   */
  private getLocalNetworkConfigs() {
    return [
      {
        chainId: '0x539', // 1337 - Hardhat default
        chainName: 'Hardhat Local',
        nativeCurrency: {
          name: 'Ethereum',
          symbol: 'ETH',
          decimals: 18,
        },
        rpcUrls: ['http://127.0.0.1:8545'],
        blockExplorerUrls: [],
      },
      {
        chainId: '0x7A69', // 31337 - Anvil default
        chainName: 'Anvil Local',
        nativeCurrency: {
          name: 'Ethereum',
          symbol: 'ETH',
          decimals: 18,
        },
        rpcUrls: ['http://127.0.0.1:8545'],
        blockExplorerUrls: [],
      },
    ];
  }

  /**
   * Detect current network from MetaMask
   */
  async detectCurrentNetwork(): Promise<NetworkInfo> {
    try {
      await this.ensureProvider();

      let network;
      try {
        network = await this.provider!.getNetwork();
      } catch (e: any) {
        const msg = (e?.message || '').toLowerCase();
        if (msg.includes('network changed')) {
          // transient during chain switch; retry once shortly after
          await new Promise(r => setTimeout(r, 400));
          network = await this.provider!.getNetwork();
        } else {
          throw e;
        }
      }
      const currentChainId = `0x${network.chainId.toString(16)}`;

      // Check if it's Swarm devnet
      if (currentChainId === chainId) {
        return {
          chainId: currentChainId,
          name: chainName,
          isSupported: true,
          isSwarmDevnet: true,
          rpcUrl: chainRPC,
          blockExplorerUrl: blockExplorer,
        };
      }

      // Treat Base mainnet (0x2105) as supported for bridging flows to prevent auto-switch flicker
      if (currentChainId.toLowerCase() === '0x2105') {
        return {
          chainId: currentChainId,
          name: 'Base',
          isSupported: true,
          isSwarmDevnet: false,
          rpcUrl: 'https://mainnet.base.org',
          blockExplorerUrl: 'https://basescan.org',
        };
      }

      // Check if it's a supported local network in development mode
      if (this.developmentMode) {
        const localConfigs = this.getLocalNetworkConfigs();
        const localNetwork = localConfigs.find(
          config => config.chainId === currentChainId
        );

        if (localNetwork) {
          return {
            chainId: currentChainId,
            name: localNetwork.chainName,
            isSupported: true,
            isSwarmDevnet: false,
            rpcUrl: localNetwork.rpcUrls[0],
            blockExplorerUrl: localNetwork.blockExplorerUrls[0],
          };
        }
      }

      // Unsupported network
      return {
        chainId: currentChainId,
        name: this.getNetworkName(currentChainId),
        isSupported: false,
        isSwarmDevnet: false,
      };
    } catch (error: any) {
      const msg = (error?.message || '').toLowerCase();
      // Fallback to window.ethereum.chainId to avoid noisy errors during chain switches
      const eth: any = (typeof window !== 'undefined' && (window as any).ethereum) ? (window as any).ethereum : null;
      const hex = eth?.chainId;
      if (hex && typeof hex === 'string') {
        return {
          chainId: hex,
          name: this.getNetworkName(hex),
          isSupported: hex === chainId,
          isSwarmDevnet: hex === chainId,
          rpcUrl: chainRPC,
          blockExplorerUrl: blockExplorer,
        };
      }
      throw new Error(
        `Failed to detect network: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get human-readable network name for common networks
   */
  private getNetworkName(chainId: string): string {
    const networkNames: Record<string, string> = {
      '0x1': 'Ethereum Mainnet',
      '0x5': 'Goerli Testnet',
      '0xaa36a7': 'Sepolia Testnet',
      '0x89': 'Polygon Mainnet',
      '0x13881': 'Polygon Mumbai',
      '0xa4b1': 'Arbitrum One',
      '0x539': 'Hardhat Local',
      '0x7A69': 'Anvil Local',
    };

    return networkNames[chainId] || `Unknown Network (${chainId})`;
  }

  /**
   * Switch to Swarm devnet using MetaMask with enhanced error handling
   */
  async switchToSwarmDevnet(): Promise<NetworkServiceResult> {
    const context = {
      operation: 'switchToSwarmDevnet',
      chainId: chainId,
      networkName: chainName,
    };

    try {
      // Check connectivity first
      const isConnected = await this.connectivityChecker.checkConnectivity(
        this.recoveryOptions
      );
      if (!isConnected) {
        throw new NetworkError(
          'MetaMask connectivity check failed',
          'CONNECTIVITY_CHECK_FAILED',
          context,
          true,
          'Unable to connect to MetaMask',
          [
            'Check MetaMask is installed and unlocked',
            'Refresh the page',
            'Try again',
          ]
        );
      }

      await this.ensureProvider();

      // Get working RPC endpoint
      const workingRpcUrl = await RpcEndpointManager.getSwarmDevnetEndpoint();

      // First try to switch to existing network
      try {
        await window.ethereum!.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainId }],
        });

        return { success: true };
      } catch (switchError: any) {
        // If network doesn't exist (error code 4902), add it
        if (switchError.code === 4902) {
          return await this.addSwarmDevnetToWallet();
        }

        // Handle other switch errors with enhanced error handling
        const networkError =
          await NetworkSwitchingHandler.handleNetworkSwitchError(
            switchError,
            {
              chainId: chainId,
              name: chainName,
              isSupported: true,
              isSwarmDevnet: true,
              rpcUrl: workingRpcUrl,
            },
            context
          );

        return await NetworkErrorRecovery.recoverFromNetworkError(
          networkError,
          this.recoveryOptions
        );
      }
    } catch (error) {
      console.error('Error switching to Swarm devnet:', error);

      if (error instanceof NetworkError) {
        return await NetworkErrorRecovery.recoverFromNetworkError(
          error,
          this.recoveryOptions
        );
      }

      // Handle unexpected errors
      const networkError = new NetworkError(
        error instanceof Error ? error.message : 'Failed to switch network',
        'UNEXPECTED_SWITCH_ERROR',
        context,
        true,
        'An unexpected error occurred while switching networks',
        ManualNetworkInstructions.generateTroubleshootingSteps()
      );

      return await NetworkErrorRecovery.recoverFromNetworkError(
        networkError,
        this.recoveryOptions
      );
    }
  }

  /**
   * Add Swarm devnet to MetaMask wallet with enhanced error handling
   */
  async addSwarmDevnetToWallet(): Promise<NetworkServiceResult> {
    const context = {
      operation: 'addSwarmDevnetToWallet',
      chainId: chainId,
      networkName: chainName,
    };

    try {
      await this.ensureProvider();

      // Get working RPC endpoint for network config
      const workingRpcUrl = await RpcEndpointManager.getSwarmDevnetEndpoint();

      const networkConfig = {
        ...this.getSwarmDevnetConfig(),
        rpcUrls: [workingRpcUrl], // Use working RPC endpoint
      };

      await window.ethereum!.request({
        method: 'wallet_addEthereumChain',
        params: [networkConfig],
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error adding Swarm devnet to wallet:', error);

      const networkError = await NetworkSwitchingHandler.handleNetworkAddError(
        error,
        {
          chainId: chainId,
          name: chainName,
          isSupported: true,
          isSwarmDevnet: true,
          rpcUrl: chainRPC,
        },
        context
      );

      return await NetworkErrorRecovery.recoverFromNetworkError(
        networkError,
        this.recoveryOptions
      );
    }
  }

  /**
   * Add local development network to MetaMask (development mode only)
   */
  async addLocalNetwork(
    rpcUrl: string = 'http://127.0.0.1:8545'
  ): Promise<NetworkServiceResult> {
    if (!this.developmentMode) {
      return {
        success: false,
        error: 'Local networks only available in development mode',
      };
    }

    try {
      await this.ensureProvider();

      // Determine chain ID based on RPC URL
      let networkConfig;
      if (rpcUrl.includes('8545')) {
        networkConfig = this.getLocalNetworkConfigs()[0]; // Hardhat
      } else {
        networkConfig = this.getLocalNetworkConfigs()[1]; // Anvil
      }

      await window.ethereum!.request({
        method: 'wallet_addEthereumChain',
        params: [{ ...networkConfig, rpcUrls: [rpcUrl] }],
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error adding local network:', error);

      if (error.code === 4001) {
        return {
          success: false,
          error: 'User rejected adding local network',
        };
      }

      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to add local network',
      };
    }
  }

  /**
   * Validate if current network is suitable for deposit operations
   */
  async validateNetworkForDeposit(): Promise<boolean> {
    try {
      const networkInfo = await this.detectCurrentNetwork();

      // In production, only Swarm devnet is supported
      if (!this.developmentMode) {
        return networkInfo.isSwarmDevnet;
      }

      // In development, both Swarm devnet and local networks are supported
      return networkInfo.isSupported;
    } catch (error) {
      console.error('Error validating network for deposit:', error);
      return false;
    }
  }

  /**
   * Get development mode status
   */
  isDevelopmentMode(): boolean {
    return this.developmentMode;
  }

  /**
   * Listen for network changes
   */
  onNetworkChange(callback: (networkInfo: NetworkInfo) => void): () => void {
    if (!window.ethereum) {
      throw new Error('MetaMask not available');
    }

    const handleChainChanged = async (chainId: string) => {
      try {
        const networkInfo = await this.detectCurrentNetwork();
        callback(networkInfo);
      } catch (error) {
        console.error('Error handling network change:', error);
      }
    };

    window.ethereum.on('chainChanged', handleChainChanged);

    // Return cleanup function
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      }
    };
  }

  /**
   * Get block explorer URL for transaction
   */
  getBlockExplorerUrl(txHash: string, networkInfo?: NetworkInfo): string {
    const explorer = networkInfo?.blockExplorerUrl || blockExplorer;

    if (!explorer) {
      return '';
    }

    return `${explorer}/tx/${txHash}`;
  }

  /**
   * Check MetaMask connectivity with enhanced retry mechanism
   */
  async checkConnectivity(maxRetries?: number): Promise<boolean> {
    const retries = maxRetries || this.recoveryOptions.maxRetries;
    return await this.connectivityChecker.checkConnectivity({
      ...this.recoveryOptions,
      maxRetries: retries,
    });
  }

  /**
   * Enhanced connectivity check with detailed error information
   */
  async checkConnectivityWithDetails(): Promise<{
    isConnected: boolean;
    error?: NetworkError;
    rpcStatus?: { [endpoint: string]: boolean };
  }> {
    try {
      const isConnected = await this.connectivityChecker.checkConnectivity(
        this.recoveryOptions
      );

      if (!isConnected) {
        return {
          isConnected: false,
          error: new NetworkError(
            'MetaMask connectivity check failed',
            'CONNECTIVITY_CHECK_FAILED',
            { operation: 'checkConnectivityWithDetails' },
            true,
            'Unable to connect to MetaMask',
            [
              'Check MetaMask is installed and unlocked',
              'Refresh the page',
              'Try again',
            ]
          ),
        };
      }

      // Test RPC endpoints
      const rpcStatus: { [endpoint: string]: boolean } = {};
      const swarmEndpoints = [
        'https://rpc.devnet.swarmind.ai',
        'https://rpc-backup.devnet.swarmind.ai',
      ];

      for (const endpoint of swarmEndpoints) {
        rpcStatus[endpoint] =
          await this.connectivityChecker.testRpcEndpoint(endpoint);
      }

      return {
        isConnected: true,
        rpcStatus,
      };
    } catch (error) {
      return {
        isConnected: false,
        error:
          error instanceof NetworkError
            ? error
            : new NetworkError(
                'Connectivity check failed',
                'CONNECTIVITY_CHECK_ERROR',
                { operation: 'checkConnectivityWithDetails' },
                true
              ),
      };
    }
  }
}

// Export singleton instance
export const networkService = new NetworkService();
